import type { AnalyticsBrowser, SegmentEvent } from '@segment/analytics-next'

type DestinationMiddleware = Parameters<
  AnalyticsBrowser['addDestinationMiddleware']
>[1]

type Obj = Record<string, unknown>

export type GtmUserTraits = {
  email?: string
  first_name?: string
  last_name?: string
}

// Both the shopper session user and the admin profile have this shape.
type NamedUser = {
  email: string
  firstName: string
  lastName: string
}

export function toGtmUserTraits(
  user: NamedUser | null | undefined,
): GtmUserTraits | undefined {
  if (!user) {
    return undefined
  }

  return {
    email: user.email,
    first_name: user.firstName,
    last_name: user.lastName,
  }
}

// Module state rather than React state: the middleware runs inside Segment's pipeline,
// outside any component, and reads whatever the signed-in user was when the event left.
let userTraits: GtmUserTraits = {}

export function setGtmUserTraits(traits: GtmUserTraits): void {
  userTraits = traits
}

export function clearGtmUserTraits(): void {
  userTraits = {}
}

function isObject(value: unknown): value is Obj {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function toObject(value: unknown): Obj {
  return isObject(value) ? value : {}
}

function getStoredTraits(): Obj {
  try {
    const parsed: unknown = JSON.parse(
      localStorage.getItem('ajs_user_traits') ?? 'null',
    )

    return toObject(parsed)
  } catch {
    return {}
  }
}

function compact(obj: Obj): Obj {
  return Object.fromEntries(
    Object.entries(obj).filter(
      ([, value]) =>
        value !== undefined &&
        !(isObject(value) && Object.keys(value).length === 0),
    ),
  )
}

// The page's own locale, not the browser's: Segment's `context.locale` is
// navigator.language, which says nothing about which locale the user is viewing.
function getPageLocale(): string | undefined {
  if (typeof document === 'undefined') {
    return undefined
  }

  return document.documentElement.lang || undefined
}

// Mirrors mb3-frontend's GTM middleware so both apps hand GTM the same
// `properties.analyticsjs` shape, and tags built against MB3's container carry over.
// `mb` (region/locale) has no mTag equivalent and is omitted.
export function createGtmEvent(event: SegmentEvent): SegmentEvent {
  const properties = toObject(event.properties)
  const context = toObject(event.context)
  const traits = {
    ...getStoredTraits(),
    ...toObject(context.traits),
    ...toObject(event.traits),
  }
  const timestamp =
    event.timestamp instanceof Date
      ? event.timestamp.getTime()
      : event.timestamp

  const analyticsjs = compact({
    messageId: event.messageId,
    userId: event.userId,
    anonymousId: event.anonymousId,
    locale: getPageLocale() ?? context.locale,
    page: toObject(context.page),
    traits,
    userTraits,
    timestamp,
  })

  return {
    ...event,
    context,
    properties: { ...properties, analyticsjs },
  } as SegmentEvent
}

export const gtmDestinationMiddleware: DestinationMiddleware = ({
  payload,
  next,
}) => {
  payload.obj = createGtmEvent(payload.obj)
  next(payload)
}
